import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'

import { Alert } from './Alert'
import { cn, focusRing } from './cn'
import { X } from './icons'

export interface ToastProps {
  readonly isOpen: boolean
  readonly onDismiss: () => void
  readonly severity?: 'success' | 'info'
  /** How long it stays before it dismisses itself; the time stops while it is hovered or focused. */
  readonly durationMs?: number
  readonly children: ReactNode
}

type Edge = 'bottom' | 'top'

/**
 * A short message at the window's bottom right, over the page: the mockups' Alert on a solid Paper
 * surface with a shadow, so it reads over whatever is behind it in both schemes. It renders at the
 * end of `body`, so an ancestor's backdrop-filter or transform cannot move it. Its status region stays
 * in place while closed, so the message is announced when it appears, and it never takes focus.
 *
 * While a modal dialog hides the rest of the page from assistive technology, it waits, so it is
 * announced once the dialog has gone. It never covers the control that has focus (WCAG 2.4.11): it
 * moves to the top of the window, or, when it would cover it there too, as at 320 px, it goes.
 */
export function Toast({ isOpen, onDismiss, severity = 'success', durationMs = 8000, children }: ToastProps) {
  const intl = useIntl()
  const [isHovered, setHovered] = useState(false)
  const [isFocused, setFocused] = useState(false)
  const [edge, setEdge] = useState<Edge>('bottom')
  const enter = useCallback(() => setHovered(true), [])
  const leave = useCallback(() => setHovered(false), [])
  const focus = useCallback(() => setFocused(true), [])
  const blur = useCallback(() => setFocused(false), [])
  const regionRef = useRef<HTMLOutputElement>(null)
  const surfaceRef = useRef<HTMLDivElement>(null)
  // Set when it moves to the other edge to clear the focused control, until it is seen to have.
  const justMoved = useRef(false)
  const isHidden = useHiddenFromAssistiveTechnology(regionRef)
  const isShown = isOpen && !isHidden
  // Closed while hovered or focused, it gets no leave or blur, so the next one starts unheld.
  if (!isShown && (isHovered || isFocused || edge !== 'bottom')) {
    setHovered(false)
    setFocused(false)
    setEdge('bottom')
  }
  const isHeld = isHovered || isFocused

  useEffect(() => {
    if (!isShown || isHeld) {
      return undefined
    }
    const timer = window.setTimeout(onDismiss, durationMs)
    return () => window.clearTimeout(timer)
  }, [isShown, isHeld, durationMs, onDismiss])

  // Before it paints, and again whenever focus or the page moves under it.
  useLayoutEffect(() => {
    const surface = surfaceRef.current
    if (!isShown || surface === null) {
      return undefined
    }
    const uncover = () => {
      const focused = document.activeElement
      if (!(focused instanceof HTMLElement) || focused === document.body || surface.contains(focused)) {
        return
      }
      const target = focused.getBoundingClientRect()
      const here = surface.getBoundingClientRect()
      if (!overlaps(target, here)) {
        justMoved.current = false
        return
      }
      // Still over it right after moving, where the move was meant to clear it: it goes rather than move back and forth.
      if (justMoved.current) {
        justMoved.current = false
        onDismiss()
        return
      }
      const there = edge === 'bottom' ? atTop(here, window.innerHeight - here.bottom) : atBottom(here, here.top)
      if (overlaps(target, there)) {
        onDismiss()
      } else {
        justMoved.current = true
        setEdge(edge === 'bottom' ? 'top' : 'bottom')
      }
    }
    uncover()
    document.addEventListener('focusin', uncover)
    window.addEventListener('scroll', uncover, { capture: true, passive: true })
    window.addEventListener('resize', uncover, { passive: true })
    return () => {
      document.removeEventListener('focusin', uncover)
      window.removeEventListener('scroll', uncover, { capture: true })
      window.removeEventListener('resize', uncover)
    }
  }, [isShown, edge, onDismiss])

  return createPortal(
    // <output> carries role="status" semantics without ARIA.
    <output
      ref={regionRef}
      className={cn('pointer-events-none fixed left-4 right-4 z-50 flex justify-end', edge === 'bottom' ? 'bottom-4' : 'top-4')}
    >
      {isShown && (
        <div
          ref={surfaceRef}
          onMouseEnter={enter}
          onMouseLeave={leave}
          onFocus={focus}
          onBlur={blur}
          className="pointer-events-auto relative w-full max-w-sm rounded bg-background shadow-card-hover"
        >
          {/* The status region above announces it, so the Alert is not a second live region. */}
          <Alert severity={severity} role="none" className="pr-12">
            {children}
          </Alert>
          <button
            type="button"
            onClick={onDismiss}
            aria-label={intl.formatMessage({ id: 'ui.toast.close' })}
            className={cn(
              'absolute right-2 top-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded text-text-secondary transition hover:text-text-primary',
              focusRing,
            )}
          >
            <X size={18} />
          </button>
        </div>
      )}
    </output>,
    document.body,
  )
}

/**
 * Whether the region is hidden from assistive technology, as a modal dialog hides the rest of the
 * page while it is open: a message put in it then would never be announced.
 */
function useHiddenFromAssistiveTechnology(ref: RefObject<HTMLElement | null>): boolean {
  const [isHidden, setHidden] = useState(false)
  useLayoutEffect(() => {
    const region = ref.current
    if (region === null) {
      return undefined
    }
    const check = () => setHidden(region.closest('[aria-hidden="true"], [inert]') !== null)
    check()
    const observer = new MutationObserver(check)
    observer.observe(region, { attributes: true, attributeFilter: ['aria-hidden', 'inert'] })
    return () => observer.disconnect()
  }, [ref])
  return isHidden
}

interface Box {
  readonly left: number
  readonly right: number
  readonly top: number
  readonly bottom: number
}

function hasArea(box: Box): boolean {
  return box.right > box.left && box.bottom > box.top
}

/** Whether two boxes share any area; an element with no box, such as one not laid out, covers nothing. */
function overlaps(a: Box, b: Box): boolean {
  return hasArea(a) && hasArea(b) && a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom
}

/** Where the box would be at the window's top, `gap` from it: the gap it has now, which scales with the root font size. */
function atTop(box: Box, gap: number): Box {
  return { left: box.left, right: box.right, top: gap, bottom: gap + box.bottom - box.top }
}

/** Where the box would be at the window's bottom, `gap` from it. */
function atBottom(box: Box, gap: number): Box {
  return { left: box.left, right: box.right, top: window.innerHeight - gap - (box.bottom - box.top), bottom: window.innerHeight - gap }
}
